import { useState, useEffect, useRef, useContext, createContext, useCallback } from 'react';
import {
  ArrowDown,
  Send,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  Radio,
  Bot,
  User,
  Server,
  Globe,
  X,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { JsonSyntax } from '@reflex/ui/components/content/log-viewer';
import { useAxon, useAxonEvents, usePublishToAxon } from './useAxons';
import { useAxonStreamSocket } from './useAxonStreamSocket';
import { useAutoScroll } from '@reflex/ui/hooks/useAutoScroll';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import { useMarqueeSelection, type MarqueeRect } from './useMarqueeSelection';
import { formatDateTime, truncate } from '@reflex/ui/lib/format';
import { Timestamp } from '@reflex/ui/components/shared/Timestamp';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { AxonViewContext } from '@reflex/ui/components/agent/AxonViewContext';
import { CopyButton } from '@reflex/ui/components/shared/CopyButton';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { PayloadToggle, type PayloadViewMode } from '@reflex/ui/components/stream/PayloadToggle';
import { buildFullMessage } from '@reflex/ui/client/event-utils';
import { isClaudeProtocolEvent, isAcpProtocolEvent } from '@reflex/shared';
import type {
  AxonEvent,
  AxonEventType,
  AxonEventOriginValue,
  PublishEventOrigin,
  PublishParams,
} from '@reflex/shared';

type OriginKind = 'agent' | 'user' | 'system' | 'external';

const ORIGIN_ICON: Record<
  OriginKind,
  { icon: React.ReactNode; className: string; border: string }
> = {
  agent: {
    icon: <Bot className="h-3 w-3" />,
    className: 'text-status-accent-violet',
    border: 'border-l-status-accent-violet/60',
  },
  user: {
    icon: <User className="h-3 w-3" />,
    className: 'text-status-accent-sky',
    border: 'border-l-status-accent-sky/60',
  },
  system: {
    icon: <Server className="h-3 w-3" />,
    className: 'text-status-neutral',
    border: 'border-l-status-neutral/60',
  },
  external: {
    icon: <Globe className="h-3 w-3" />,
    className: 'text-status-warning',
    border: 'border-l-status-warning/60',
  },
};

function resolveOriginKind(event: AxonEvent): OriginKind {
  if (event.origin) {
    const map: Record<AxonEventOriginValue, OriginKind> = {
      AGENT_EVENT: 'agent',
      USER_EVENT: 'user',
      SYSTEM_EVENT: 'system',
      EXTERNAL_EVENT: 'external',
    };
    return map[event.origin] ?? 'system';
  }
  const t = event.type;
  if (t === 'query' || t === 'session/prompt') return 'user';
  if (t.startsWith('agent.') || isClaudeProtocolEvent(t) || isAcpProtocolEvent(t)) return 'agent';
  if (t.startsWith('turn.') || t.startsWith('flow.')) return 'system';
  if (t === 'webhook') return 'external';
  if (t === 'message') return 'agent';
  return 'system';
}

const ORIGIN_STYLE: Record<AxonEventOriginValue, { label: string; className: string }> = {
  AGENT_EVENT: {
    label: 'agent',
    className:
      'bg-status-accent-violet/10 text-status-accent-violet border-status-accent-violet/20',
  },
  USER_EVENT: {
    label: 'user',
    className: 'bg-status-accent-sky/10 text-status-accent-sky border-status-accent-sky/20',
  },
  EXTERNAL_EVENT: {
    label: 'external',
    className: 'bg-status-warning/10 text-status-warning border-status-warning/20',
  },
  SYSTEM_EVENT: {
    label: 'system',
    className: 'bg-status-neutral/10 text-status-neutral border-status-neutral/20',
  },
};

function getEventPreview(type: AxonEventType, payload: unknown): React.ReactNode | null {
  if (payload == null) return null;
  const p =
    typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : null;

  switch (type) {
    case 'agent.started': {
      if (!p) break;
      return (
        <span className="flex items-center gap-2">
          {typeof p.name === 'string' && (
            <span className="font-medium text-foreground/90">{p.name}</span>
          )}
          {typeof p.agentType === 'string' && (
            <span className="rounded bg-muted px-1.5 py-px text-[10px] font-mono text-muted-foreground">
              {p.agentType}
            </span>
          )}
          {typeof p.devboxId === 'string' && (
            <span className="text-muted-foreground font-mono">{truncate(p.devboxId, 20)}</span>
          )}
        </span>
      );
    }
    case 'agent.stopped':
    case 'agent.killed': {
      if (p && typeof p.agentId === 'string') {
        return <span className="font-mono text-muted-foreground">{truncate(p.agentId, 32)}</span>;
      }
      break;
    }
    case 'agent.error': {
      if (p && typeof p.error === 'string') {
        return <span className="text-status-error/80">{truncate(p.error, 120)}</span>;
      }
      break;
    }
    case 'message': {
      if (!p) break;
      const role = typeof p.role === 'string' ? p.role : undefined;
      const msg = typeof p.message === 'string' ? p.message : null;
      if (!msg) break;
      return (
        <span className="flex items-center gap-1.5">
          {role && (
            <span
              className={`rounded px-1 py-px text-[10px] font-medium ${role === 'agent' ? 'bg-status-accent-violet/10 text-status-accent-violet' : 'bg-status-accent-sky/10 text-status-accent-sky'}`}
            >
              {role}
            </span>
          )}
          <span className="text-foreground/70">{truncate(msg.replace(/\n/g, ' '), 120)}</span>
        </span>
      );
    }
    case 'agent.pr_created': {
      if (!p) break;
      const prUrl = typeof p.url === 'string' ? p.url : null;
      const prNum = typeof p.number === 'number' ? `#${p.number}` : '';
      const prTitle = typeof p.title === 'string' ? p.title : '';
      return (
        <span className="text-status-accent-indigo/80">
          {[prNum, prTitle, prUrl].filter(Boolean).join(' — ')}
        </span>
      );
    }
    case 'webhook':
      break;
  }

  // ACP event previews
  if (isAcpProtocolEvent(type) && p) {
    const update = p.update as Record<string, unknown> | undefined;
    if (update) {
      const content = update.content as Record<string, unknown> | undefined;
      if (content?.type === 'text' && typeof content.text === 'string') {
        return (
          <span className="text-foreground/70">
            {truncate((content.text as string).replace(/\n/g, ' '), 100)}
          </span>
        );
      }
      if (update.sessionUpdate === 'tool_call') {
        const kind = typeof update.kind === 'string' ? update.kind : '';
        const title = typeof update.title === 'string' ? update.title : '';
        return (
          <span className="text-status-warning/80">
            {[kind, title].filter(Boolean).join(': ') || 'tool_call'}
          </span>
        );
      }
      if (update.sessionUpdate === 'plan' && Array.isArray(update.steps)) {
        return <span className="text-status-accent-indigo/80">{update.steps.length} steps</span>;
      }
    }
  }

  if (typeof payload === 'string') {
    return (
      <span className="text-muted-foreground">{truncate(payload.replace(/\n/g, ' '), 100)}</span>
    );
  }
  if (p) {
    return (
      <span className="font-mono text-muted-foreground">
        {truncate(JSON.stringify(payload), 100)}
      </span>
    );
  }
  return null;
}

interface MarqueeContextValue {
  wasDragRef: React.MutableRefObject<boolean>;
  toggle: (id: string) => void;
}

const MarqueeContext = createContext<MarqueeContextValue | null>(null);

function EventRow({
  event,
  highlighted = false,
  selected = false,
}: {
  event: AxonEvent;
  highlighted?: boolean;
  selected?: boolean;
}) {
  const [expanded, setExpanded] = useState(highlighted);
  const [viewMode, setViewMode] = useState<PayloadViewMode>('payload');
  const nav = useContext(AxonViewContext);
  const marquee = useContext(MarqueeContext);
  const payloadStr =
    event.payload != null
      ? typeof event.payload === 'string'
        ? event.payload
        : JSON.stringify(event.payload, null, 2)
      : null;
  const preview = getEventPreview(event.type, event.payload);
  const originKind = resolveOriginKind(event);
  const originIcon = ORIGIN_ICON[originKind] ?? ORIGIN_ICON.system;

  const fullMsg = buildFullMessage(event);
  const displayValue = viewMode === 'full' ? fullMsg : event.payload;
  const displayStr = viewMode === 'full' ? JSON.stringify(fullMsg, null, 2) : payloadStr;

  const handleClick = (e: React.MouseEvent) => {
    if (marquee?.wasDragRef.current) return;
    if (e.metaKey || e.ctrlKey) {
      marquee?.toggle(event.id);
      return;
    }
    if (payloadStr) setExpanded(!expanded);
  };

  return (
    <div
      id={`event-${event.id}`}
      data-event-id={event.id}
      data-testid="axon-event-row"
      data-selected={selected || undefined}
      className={`border-b border-border/40 transition-colors ${highlighted ? 'border-l-[3px] border-l-status-accent-sky bg-status-accent-sky/15 ring-1 ring-inset ring-status-accent-sky/30 animate-highlight-fade' : selected ? `border-l-2 ${originIcon.border} bg-status-accent-sky/5 ring-1 ring-inset ring-status-accent-sky/40` : `border-l-2 ${originIcon.border} hover:bg-muted/20`}`}
    >
      <div
        role="group"
        aria-label="Event row"
        onClick={handleClick}
        onDoubleClick={() => nav?.showInChatView(event.id)}
        className={`w-full text-left px-3 py-2 ${payloadStr ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2.5 text-xs">
          {payloadStr && (
            <button
              type="button"
              className="sr-only"
              onClick={() => setExpanded(!expanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpanded(!expanded);
                }
              }}
            >
              {expanded ? 'Collapse event' : 'Expand event'}
            </button>
          )}
          <span
            role="toolbar"
            aria-label="Event actions"
            className="shrink-0 inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Timestamp timestamp={event.timestamp} className="font-mono text-muted-foreground" />
            <EventDetailTooltip event={event} onGoToEvent={nav?.showInChatView} />
          </span>
          <span className="font-mono text-[10px] text-muted-foreground w-8 shrink-0 text-right">
            {event.sequence != null ? `#${event.sequence}` : ''}
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] font-medium text-muted-foreground shrink-0">
            {event.type}
          </span>
          {event.origin && (
            <span
              className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] shrink-0 ${ORIGIN_STYLE[event.origin]?.className ?? 'bg-muted text-muted-foreground border-border'}`}
            >
              <span className={originIcon.className}>{originIcon.icon}</span>
              {ORIGIN_STYLE[event.origin]?.label ?? event.origin}
            </span>
          )}
          {event.source && (
            <span className="rounded-md bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground shrink-0">
              {event.source}
            </span>
          )}
          {preview && <span className="truncate text-[12px] min-w-0">{preview}</span>}
          {payloadStr && (
            <span className="ml-auto shrink-0 text-muted-foreground">
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
          )}
        </div>
      </div>
      {expanded && payloadStr && (
        <div className="px-3 pb-2">
          <div className="flex items-center justify-between mb-1.5 ml-26">
            <PayloadToggle mode={viewMode} onToggle={setViewMode} />
            <CopyButton text={displayStr ?? ''} />
          </div>
          <pre className="rounded-md bg-muted/40 border border-border/60 p-2.5 text-[11px] text-muted-foreground overflow-x-auto max-h-64 overflow-y-auto ml-26">
            {displayValue != null && typeof displayValue === 'object' ? (
              <JsonSyntax value={displayValue} />
            ) : (
              displayStr
            )}
          </pre>
        </div>
      )}
    </div>
  );
}

function CopyAllButton({ events }: { events: AxonEvent[] }) {
  const { copied, copy } = useCopyToClipboard();

  if (events.length === 0) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      data-testid="axon-copy-all"
      className="h-7 gap-1 bg-background px-2 text-[10px] shadow-sm"
      onClick={() => copy(JSON.stringify(events, null, 2))}
    >
      {copied ? <Check className="h-3 w-3 text-status-success" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied!' : `Copy all (${events.length})`}
    </Button>
  );
}

function CopySelectedButton({
  events,
  selectedIds,
  onClear,
}: {
  events: AxonEvent[];
  selectedIds: Set<string>;
  onClear: () => void;
}) {
  const { copied, copy } = useCopyToClipboard();
  const selectedEvents = events.filter((e) => selectedIds.has(e.id));

  if (selectedEvents.length === 0) return null;

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-status-accent-sky/30 bg-status-accent-sky/10">
      <Button
        variant="ghost"
        size="sm"
        data-testid="axon-copy-selected"
        className="h-6 gap-1 px-2 text-[10px] text-status-accent-sky hover:text-status-accent-sky hover:bg-status-accent-sky/10"
        onClick={() => copy(JSON.stringify(selectedEvents, null, 2))}
      >
        {copied ? <Check className="h-3 w-3 text-status-success" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied!' : `Copy selected (${selectedEvents.length})`}
      </Button>
      <button
        type="button"
        aria-label="Clear selection"
        data-testid="axon-clear-selection"
        onClick={onClear}
        className="flex h-6 w-5 items-center justify-center text-status-accent-sky/80 hover:text-status-accent-sky"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function MarqueeOverlay({ rect }: { rect: MarqueeRect | null }) {
  if (!rect) return null;
  return (
    <div
      data-testid="axon-marquee-overlay"
      className="pointer-events-none absolute z-20 rounded-sm border border-status-accent-sky/60 bg-status-accent-sky/10"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
    />
  );
}

export function AxonEventList({
  events,
  highlightEventId,
}: {
  events: AxonEvent[];
  highlightEventId?: string;
}) {
  const { containerRef, contentRef, showScrollBtn, onScrollBtnClick } = useAutoScroll(
    highlightEventId ? { initiallyPinned: false } : undefined,
  );
  const hasScrolledRef = useRef(false);
  const marquee = useMarqueeSelection(containerRef, events);
  const { copy: copySelectedToClipboard } = useCopyToClipboard();

  useEffect(() => {
    if (!highlightEventId || hasScrolledRef.current || events.length === 0) return;
    if (!events.some((e) => e.id === highlightEventId)) return;

    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-event-id="${CSS.escape(highlightEventId)}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      hasScrolledRef.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, [highlightEventId, events, containerRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && marquee.selectedIds.size > 0) {
        marquee.clear();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'C')) {
        if (marquee.selectedIds.size === 0) return;
        const target = e.target as HTMLElement | null;
        if (target && target.closest('input, textarea, select, [contenteditable="true"]')) {
          return;
        }
        const selectedEvents = events.filter((ev) => marquee.selectedIds.has(ev.id));
        if (selectedEvents.length === 0) return;
        e.preventDefault();
        copySelectedToClipboard(JSON.stringify(selectedEvents, null, 2));
      }
    },
    [marquee, events, copySelectedToClipboard],
  );

  const hasSelection = marquee.selectedIds.size > 0;

  return (
    <MarqueeContext.Provider value={{ wasDragRef: marquee.wasDragRef, toggle: marquee.toggle }}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {events.length > 0 && (
          <div className="flex shrink-0 items-center justify-end border-b border-border/40 bg-background px-3 py-1.5">
            {hasSelection ? (
              <CopySelectedButton
                events={events}
                selectedIds={marquee.selectedIds}
                onClear={marquee.clear}
              />
            ) : (
              <CopyAllButton events={events} />
            )}
          </div>
        )}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <MarqueeOverlay rect={marquee.marqueeRect} />
          <div
            ref={containerRef}
            className={`h-full overflow-y-auto ${marquee.isDragging ? 'select-none' : ''}`}
            role="log"
            aria-label="Axon events"
            tabIndex={-1}
            onMouseDown={marquee.onMouseDown}
            onKeyDown={handleKeyDown}
          >
            <div ref={contentRef}>
              {events.length === 0 && (
                <EmptyState
                  icon={<Radio className="h-5 w-5" />}
                  title="No events yet"
                  description="Publish an event or wait for incoming events."
                />
              )}
              {events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  highlighted={highlightEventId === event.id}
                  selected={marquee.selectedIds.has(event.id)}
                />
              ))}
            </div>
          </div>

          {showScrollBtn && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-full shadow-md"
                onClick={onScrollBtnClick}
                aria-label="Scroll to bottom"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </MarqueeContext.Provider>
  );
}

function PublishForm({ axonId }: { axonId: string }) {
  const publish = usePublishToAxon();
  const [eventType, setEventType] = useState('');
  const [source, setSource] = useState('reflex');
  const [origin, setOrigin] = useState<PublishEventOrigin>('USER_EVENT');
  const [payload, setPayload] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const type = eventType.trim();
    if (!type) return;

    publish.mutate(
      {
        axonId,
        params: {
          event_type: type as PublishParams['event_type'],
          source: source.trim() || 'reflex',
          origin,
          payload: payload.trim() || '{}',
        },
      },
      {
        onSuccess: () => {
          setEventType('');
          setPayload('');
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-border p-3">
      <div className="grid grid-cols-[minmax(0,1fr)_8rem_7.5rem] gap-2">
        <Input
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          placeholder="event_type (e.g. message)"
          className="h-8 min-w-0 text-xs font-mono"
        />
        <Input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="source"
          className="h-8 min-w-0 text-xs font-mono"
        />
        <select
          value={origin}
          onChange={(e) => setOrigin(e.target.value as PublishEventOrigin)}
          aria-label="Origin"
          className="h-8 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="USER_EVENT">USER</option>
          <option value="AGENT_EVENT">AGENT</option>
          <option value="EXTERNAL_EVENT">EXTERNAL</option>
        </select>
        <textarea
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder='payload JSON (e.g. {"message": "hello"})'
          rows={2}
          className="col-span-2 min-h-[4.5rem] min-w-0 resize-none rounded-md border border-input bg-background px-2 py-1.5 text-xs font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <Button
          type="submit"
          size="sm"
          className="h-8 w-full self-end"
          disabled={!eventType.trim() || publish.isPending}
        >
          <Send className="mr-1.5 h-3.5 w-3.5" />
          Publish
        </Button>
      </div>
    </form>
  );
}

interface AxonStreamViewProps {
  axonId: string;
  highlightEventId?: string;
}

export function AxonStreamView({ axonId, highlightEventId }: AxonStreamViewProps) {
  const { data: axonDetail, isLoading } = useAxon(axonId);
  const { data: events } = useAxonEvents(axonId);
  useAxonStreamSocket(axonId);

  if (isLoading || !axonDetail) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading axon stream...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <PanelTitle as="h2" size="sm" className="font-mono">
            {axonDetail.id}
          </PanelTitle>
          <CopyButton text={axonDetail.id} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
          <span>Created {formatDateTime(axonDetail.created_at_ms)}</span>
          {(axonDetail.last_event_at_ms ?? 0) > 0 && (
            <span>Last event {formatDateTime(axonDetail.last_event_at_ms!)}</span>
          )}
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
            {events?.length ?? 0} events
          </span>
        </div>
      </div>

      <AxonEventList events={events ?? []} highlightEventId={highlightEventId} />

      <PublishForm axonId={axonId} />
    </div>
  );
}
